// components/FollowButton.tsx
import { Button, Icon, Text, styled } from '@qcx/ui';
import { Form } from 'react-router';
import { FormEvent, useCallback } from 'react';
import { UserService } from '@atreus/data';
import { faBell, faBellSlash } from '@fortawesome/pro-regular-svg-icons';
import { useAuthModalStore } from '@kratos/auth';
import { useEditorialsStore } from '../editorials.store';

interface FollowButtonProps {
  editorialId: number;
}

export const FollowButton = ({ editorialId }: FollowButtonProps) => {
  const addEditorial = useEditorialsStore(s => s.add);
  const deleteEditorial = useEditorialsStore(s => s.delete);
  const toggleAuthModal = useAuthModalStore(s => s.toggle);
  const toggleSigninModal = useCallback(() => {
    toggleAuthModal('signup');
  }, [toggleAuthModal]);
  const followed = useEditorialsStore(s => s.isCurrentlyFollowed(editorialId));
  const iconSource = followed ? faBellSlash : faBell;

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      let isAuthenticated = false;

      try {
        const userService = new UserService({ credentials: 'include' });
        const user = await userService.getAtreusUser();
        isAuthenticated = !!user;
      } catch {
        isAuthenticated = false;
      }

      if (!isAuthenticated) {
        toggleSigninModal();
        return;
      }

      if (followed) {
        deleteEditorial(editorialId);
      } else {
        addEditorial(editorialId);
      }
    },
    [followed, toggleSigninModal, addEditorial, editorialId, deleteEditorial]
  );

  return (
    <Form method="post" replace onSubmit={handleSubmit}>
      <input name="editorialId" type="hidden" value={editorialId} />
      <StyledFollowButton
        name="isFollowing"
        isFollowing={followed}
        leftIcon={<Icon css={{ fontSize: 20 }} icon={iconSource} />}
        type="submit"
        value={followed.toString()}
      >
        <Text
          id="text-button"
          css={{
            ml: '$2',
            display: 'none',
            '@md': { display: 'block' }
          }}
          variant={'sm'}
          weight="medium"
        >
          {followed ? 'Deixar de Seguir' : 'Seguir instituto'}
        </Text>
      </StyledFollowButton>
    </Form>
  );
};

const StyledFollowButton = styled(Button, {
  flexCenter: true,
  rounded: '$4xl',
  color: '$white',
  px: '$4 !important',

  '& > span': {
    mr: '0 !important'
  },

  variants: {
    isFollowing: {
      true: {
        bg: '$blue50 !important',
        border: '1px solid $blue50 !important',
        color: '$blue800 !important',
        '&:hover': {
          bg: '$blue100 !important',
          border: '1px solid $blue100 !important',
          color: '$blue800 !important'
        }
      },
      false: {
        bg: '$neutral50',
        border: '1px solid $neutral50',
        color: '$neutral700',
        '&:hover': {
          bg: '$neutral200 !important',
          border: '1px solid $neutral200 !important',
          color: '$neutral700'
        }
      }
    }
  }
});
